import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { UserResponse, RegisterInput, LoginInput } from '@kundaliai/shared';
import { api } from '@/lib/api';
import { useChatStore } from '@/stores/chatStore';
import { GUEST_TOKEN_KEY } from '@/lib/guestStorage';

interface AuthState {
  user: UserResponse | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (input: LoginInput) => Promise<void>;
  signUp: (input: RegisterInput) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: (idToken: string) => Promise<void>;
  getToken: () => Promise<string | null>;
  /** Re-read the signed-in user, so an edit elsewhere shows in the header too. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthResponse {
  user: UserResponse;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * A visitor who chatted as a guest already gave us their birth details. Move
 * them onto the account so the very next screen doesn't ask again. Best effort:
 * losing the migration is a re-prompt, and must never cost the visitor the
 * sign-in they just completed. GuestProvider clears the guest session itself
 * once isSignedIn flips.
 */
async function claimGuestSessionIfAny(): Promise<void> {
  const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
  if (!guestToken) return;
  try {
    await api.claimGuestSession(guestToken);
  } catch (error) {
    console.error('Failed to claim guest session for the new account:', error);
  }
}

const ACCESS_TOKEN_KEY = 'kundaliai_access_token';
const REFRESH_TOKEN_KEY = 'kundaliai_refresh_token';
const TOKEN_EXPIRY_KEY = 'kundaliai_token_expiry';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoaded: false,
    isSignedIn: false,
  });

  // Get access token with automatic refresh if needed
  const getToken = useCallback(async (): Promise<string | null> => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!accessToken || !refreshToken) {
      return null;
    }

    // Check if token is expired or about to expire (within 1 minute)
    const expiryTime = expiry ? parseInt(expiry, 10) : 0;
    const now = Date.now();
    const oneMinute = 60 * 1000;

    if (expiryTime - now < oneMinute) {
      try {
        const tokens = await api.refreshToken(refreshToken);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        localStorage.setItem(
          TOKEN_EXPIRY_KEY,
          (Date.now() + tokens.expiresIn * 1000).toString()
        );
        return tokens.accessToken;
      } catch {
        // Refresh failed, clear tokens
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        setState({ user: null, isLoaded: true, isSignedIn: false });
        return null;
      }
    }

    return accessToken;
  }, []);

  // Set up API client token getter
  useEffect(() => {
    api.setTokenGetter(getToken);
  }, [getToken]);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!accessToken || !refreshToken) {
        setState({ user: null, isLoaded: true, isSignedIn: false });
        return;
      }

      try {
        // Try to get current user
        const user = await api.getMe();
        setState({ user, isLoaded: true, isSignedIn: true });
      } catch {
        // Token invalid, try to refresh
        try {
          const tokens = await api.refreshToken(refreshToken);
          localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
          localStorage.setItem(
            TOKEN_EXPIRY_KEY,
            (Date.now() + tokens.expiresIn * 1000).toString()
          );
          const user = await api.getMe();
          setState({ user, isLoaded: true, isSignedIn: true });
        } catch {
          // Refresh failed, clear everything
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(TOKEN_EXPIRY_KEY);
          setState({ user: null, isLoaded: true, isSignedIn: false });
        }
      }
    };

    initAuth();
  }, []);

  /**
   * Everything that has to happen between "the server accepted these
   * credentials" and "the app may render as signed in", for every entry point
   * (register, password login, Google).
   *
   * The order matters. The guest session is claimed BEFORE isSignedIn flips,
   * because the chat page reacts to that flip by fetching the profile to decide
   * whether to ask for birth details — claiming afterwards would race it and
   * prompt the visitor for details we were in the middle of migrating.
   */
  const completeAuth = async (response: AuthResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      (Date.now() + response.expiresIn * 1000).toString()
    );

    await claimGuestSessionIfAny();

    setState({
      user: response.user,
      isLoaded: true,
      isSignedIn: true,
    });
  };

  const signIn = async (input: LoginInput) => {
    await completeAuth(await api.login(input));
  };

  const signUp = async (input: RegisterInput) => {
    await completeAuth(await api.register(input));
  };

  const signOut = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    await api.logout(refreshToken || undefined);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem('kundaliai-chat');
    useChatStore.getState().clearSession();
    setState({ user: null, isLoaded: true, isSignedIn: false });
  };

  const signInWithGoogle = async (idToken: string) => {
    await completeAuth(await api.socialLogin('google', idToken));
  };

  /**
   * Pull the user again after they have changed something about their account.
   * The header renders initials from this, so without it a renamed user keeps
   * their old initials until the next full load.
   *
   * A failure here is not a sign-out: the caller has just had a successful
   * write, and dropping the session over a follow-up read would be worse than
   * showing a stale name.
   */
  const refreshUser = useCallback(async () => {
    try {
      const user = await api.getMe();
      setState((prev) => (prev.isSignedIn ? { ...prev, user } : prev));
    } catch {
      // Keep whatever we already had.
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        signOut,
        signInWithGoogle,
        getToken,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Convenience hook for getting just the user
export function useUser() {
  const { user, isLoaded, isSignedIn } = useAuth();
  return { user, isLoaded, isSignedIn };
}
